"use client";

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import * as React from "react";

import { cn } from "@/newComponents/utils/utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(" ", className)}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center  gap-2font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDown className="w-4 h-4 mb-1 transition-transform duration-200 shrink-0" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

type AccordionItemNumberProps = {
  number: number;
  hasError?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const AccordionItemNumber = React.forwardRef<
  HTMLDivElement,
  AccordionItemNumberProps
>(({ className, number, hasError, ...props }, ref) => {
  const formattedNumber = number.toString().padStart(2, "0");
  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center justify-center text-lg tracking-tightest text-sentric-dark-background font-sentric font-extrabold opacity-10  flex-shrink-0 w-8",

        hasError
        && "text-sentric-orange opacity-75 ",
        className,
      )}
      {...props}
    >
      <div>{formattedNumber}</div>
    </div>
  );
});
AccordionItemNumber.displayName = "AccordionItemNumber";

export { Accordion, AccordionContent, AccordionItem, AccordionItemNumber, AccordionTrigger };
